import React, { useState } from "react";
import { Icon } from "semantic-ui-react";
import { isMobile } from "react-device-detect";
import Utils from "../util/Utils";
import "../css/home.css";

const CATEGORY_ORDER = ["Valuation", "Growth", "Profitability", "Financial Health"];
const SCORE_MAX = 3;

// ── Design tokens (match CompanyProfile) ───────────────────────────────────────
const C = {
  textPrimary:    "#0f1f35",
  textSecondary:  "#3d5166",
  textMuted:      "#7a92a8",
  bg:             "#ffffff",
  card:           "#ffffff",
  cardHeader:     "#f5f2ed",
  cardHeaderText: "#1a2744",
  border:         "#d0dcea",
  borderLight:    "#e8eef6",
  navyDark:       "#0f2d52",
  navy:           "#1a4075",
  navyMid:        "#2c5282",
  navyLight:      "#dce8f5",
  success:        "#0a8a7e",
  successBg:      "#e6f9f7",
  warning:        "#b86b00",
  warningBg:      "#fdf3e3",
  danger:         "#b91c1c",
  dangerBg:       "#fde8e8",
};

const MONO = "'DM Mono', 'Fira Mono', 'Courier New', monospace";
const SANS = "'Lato', 'Helvetica Neue', Arial, sans-serif";

const CATEGORY_META = {
  Valuation:          { icon: "balance scale", color: "#4f46e5" },
  Growth:             { icon: "chart line",    color: "#0a8a7e" },
  Profitability:      { icon: "dollar sign",   color: "#b86b00" },
  "Financial Health": { icon: "shield",        color: "#1a4075" },
};

// ── Score helpers ─────────────────────────────────────────────────────────────
function getMeterColor(score) {
  if (score == null) return C.border;
  const s = Number(score);
  if (s >= 2.5) return C.success;
  if (s >= 1.5) return C.warning;
  if (s >= 0.5) return "#d08000";
  return C.danger;
}

function ScoreMeter({ score }) {
  const s   = score != null ? Math.min(SCORE_MAX, Math.max(0, Number(score))) : 0;
  const pct = (s / SCORE_MAX) * 100;
  const col = getMeterColor(s);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 130 }}>
      <div style={{
        flex: 1, height: 7,
        backgroundColor: C.borderLight,
        borderRadius: 4, overflow: "hidden",
      }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          backgroundColor: col, borderRadius: 4,
          transition: "width 0.4s ease",
        }} />
      </div>
      <span style={{
        fontSize: "0.96rem", fontWeight: 800,
        color: col, minWidth: 44,
        fontFamily: MONO, fontVariantNumeric: "tabular-nums",
      }}>
        {score != null
          ? (s % 1 === 0 ? `${s}/${SCORE_MAX}` : `${Number(s).toFixed(1)}/${SCORE_MAX}`)
          : "—"}
      </span>
    </div>
  );
}

function ScorePill({ score }) {
  if (score == null) return null;
  const col = getMeterColor(score);
  const val = typeof score === "number" ? score.toFixed(1) : score;
  return (
    <span style={{
      fontSize: "0.85rem", fontWeight: 700,
      color: col, fontFamily: MONO,
      padding: "0.15rem 0.55rem",
      borderRadius: 20,
      backgroundColor: `${col}15`,
      border: `1.5px solid ${col}35`,
    }}>
      {val} / 3
    </span>
  );
}

// ── Shared page header (match profile page) ────────────────────────────────────
export function PageHeader({ section, title, subtitle, right }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start",
      justifyContent: "space-between", flexWrap: "wrap",
      gap: "1rem",
      paddingBottom: "1.25rem", marginBottom: "2rem",
      borderBottom: `1px solid ${C.borderLight}`,
    }}>
      <div style={{
        borderLeft: `4px solid ${C.navy}`,
        paddingLeft: "1.25rem",
      }}>
        <h2 style={{
          margin: 0,
          fontSize: isMobile ? "1.5rem" : "1.75rem",
          fontWeight: 700,
          color: C.cardHeaderText, letterSpacing: "-0.01em",
          fontFamily: SANS, lineHeight: 1.2,
        }}>
          {title}
        </h2>
        {subtitle && (
          <p style={{
            margin: "0.35rem 0 0",
            fontSize: "0.9rem", color: C.textSecondary,
            fontFamily: SANS,
          }}>
            {subtitle}
          </p>
        )}
      </div>
      {right && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

// ── Data helpers ──────────────────────────────────────────────────────────────
function getObservationsForDimension(stockValues, dimensionName) {
  const map = {
    "Sales Growth":      ["revenueObservations"],
    "Net Income Growth": ["netIncomeObservations"],
    "Cashflow Growth":   ["operatingCashObservations"],
    "FCF Growth":        ["freeCashFlowObservations"],
    "Future EPS Growth": ["epsObservations"],
  };
  const keys = map[dimensionName];
  if (!keys) return [];
  const list = [];
  keys.forEach((key) => {
    const obs = stockValues[key];
    if (obs) {
      const arr = Array.isArray(obs) ? obs : Object.keys(obs).map((i) => obs[i]);
      arr.forEach((o) => {
        if (o && o.name
          && o.name !== "1 Yr Growth compared to 3Yr Growth"
          && o.name !== "1 Yr Growth compared to 5Yr Growth") {
          list.push(o);
        }
      });
    }
  });
  return list;
}

function buildMergedData(stockValues, scoreAudit) {
  const dimensions = scoreAudit?.dimensions || [];
  if (dimensions.length === 0) return null;
  const byCategory = {};
  CATEGORY_ORDER.forEach((c) => (byCategory[c] = []));
  dimensions.forEach((d) => {
    byCategory[d.category].push({
      ...d,
      observations: getObservationsForDimension(stockValues, d.dimensionName),
    });
  });
  return byCategory;
}

// ── Category section ──────────────────────────────────────────────────────────
function CategorySection({ title, items, categoryScore, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!items || items.length === 0) return null;

  const meta      = CATEGORY_META[title] || { icon: "tag", color: C.navyMid };
  const avgScore  = items.reduce((a, d) => a + (d.score ?? 0), 0) / items.length;
  const effective = categoryScore ?? avgScore;
  const barColor  = getMeterColor(effective);

  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      {/* Header */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between",
          flexWrap: isMobile ? "wrap" : "nowrap",
          gap: "0.5rem",
          padding: isMobile ? "0.9rem 1.1rem" : "1rem 1.5rem",
          cursor: "pointer", userSelect: "none",
          transition: "background 0.12s",
          backgroundColor: C.cardHeader,
          borderBottom: `1px solid ${C.borderLight}`,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = C.cardHeader)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = C.cardHeader)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            backgroundColor: C.bg,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
            border: `1px solid ${C.borderLight}`,
          }}>
            <Icon name={meta.icon} style={{ color: C.navy, margin: 0, fontSize: "0.95rem" }} />
          </div>
          <span style={{
            fontWeight: 700, fontSize: "1rem",
            color: C.cardHeaderText, fontFamily: SANS,
          }}>
            {title}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
          <ScorePill score={categoryScore} />
          <div style={{ width: 80, height: 6, backgroundColor: C.borderLight, borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              width: `${(effective / SCORE_MAX) * 100}%`, height: "100%",
              backgroundColor: barColor, borderRadius: 3,
              transition: "width 0.4s ease",
            }} />
          </div>
          <Icon
            name={open ? "chevron down" : "chevron right"}
            size="small"
            style={{ color: C.navy, flexShrink: 0 }}
          />
        </div>
      </div>

      {/* Expanded rows */}
      {open && (
        <div style={{ backgroundColor: C.bg, borderTop: `1px solid ${C.borderLight}` }}>
          {items.map((d, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: isMobile ? "flex-start" : "center",
                justifyContent: "space-between",
                padding: isMobile ? "0.85rem 1.1rem" : "0.9rem 1.5rem 0.9rem 2.6rem",
                borderBottom: i < items.length - 1 ? `1px solid ${C.borderLight}` : "none",
                gap: "1rem", flexWrap: isMobile ? "wrap" : "nowrap",
                transition: "background 0.1s",
                backgroundColor: "transparent",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = C.card)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                <div style={{
                  fontWeight: 600, fontSize: "0.95rem",
                  color: C.textPrimary, fontFamily: SANS, marginBottom: "0.2rem",
                }}>
                  {d.dimensionName}
                </div>
                <div style={{
                  fontSize: "0.84rem", color: C.textSecondary,
                  fontFamily: MONO, fontVariantNumeric: "tabular-nums",
                }}>
                  {d.inputValueFormatted ?? String(d.inputValue ?? "—")}
                  {d.bandsUsed && (
                    <span style={{ marginLeft: "0.4rem", color: C.textMuted }}>· {d.bandsUsed}</span>
                  )}
                </div>
                {d.observations && d.observations.length > 0 && (
                  <div style={{ fontSize: "0.8rem", color: C.textMuted, marginTop: "0.2rem", lineHeight: 1.5 }}>
                    {d.observations.map((o, j) => (
                      <span key={j}>
                        {j > 0 && <span style={{ margin: "0 0.25rem", opacity: 0.4 }}>·</span>}
                        <span style={{ color: C.textSecondary }}>{o.name}:</span>{" "}
                        {o.value != null && !Number.isNaN(Number(o.value))
                          ? Utils.format_ratio(o.value) : "—"}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ flexShrink: 0 }}>
                <ScoreMeter score={d.score} />
              </div>

              <div style={{
                fontSize: "0.84rem", color: C.textSecondary,
                maxWidth: isMobile ? "100%" : 230,
                lineHeight: 1.55, overflow: "hidden",
                textOverflow: "ellipsis", display: "-webkit-box",
                WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                flex: isMobile ? "1 1 100%" : "0 0 230px",
                fontFamily: SANS,
              }} title={d.reasoning}>
                {d.reasoning ?? "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function FA2Consolidated({ stockValues, scoreAudit }) {
  if (!stockValues) return null;

  const sr     = stockValues.stockReport || {};
  const merged = buildMergedData(stockValues, scoreAudit);

  return (
    <div style={{
      padding: isMobile ? "2rem 0.5rem" : "2rem 1rem",
      width: "100%",
      maxWidth: 1280,
      margin: "0 auto",
      boxSizing: "border-box", fontFamily: SANS,
      backgroundColor: C.bg,
    }}>

      <PageHeader
        title="Fundamental Analysis"
        subtitle={[
          "Multi-factor scoring · 0–3 per dimension · Total 0–39",
      
        ].filter(Boolean).join("  ·  ")}
        right={
          sr.totalScore != null ? (
            <div style={{ textAlign: "right" }}>
              <span style={{
                display: "block", fontSize: "0.78rem", fontWeight: 700,
                letterSpacing: "0.04em",
                color: C.textSecondary, fontFamily: SANS, marginBottom: "0.2rem",
              }}>
                Total Score
              </span>
              <span style={{
                fontSize: "2rem", fontWeight: 700,
                color: C.cardHeaderText, fontFamily: MONO,
                letterSpacing: "-0.02em", lineHeight: 1,
              }}>
                {sr.totalScore}
                <span style={{ fontSize: "0.5em", color: C.textMuted, fontWeight: 600 }}> /39</span>
              </span>
            </div>
          ) : null
        }
      />

      {/* Score breakdown */}
      {merged ? (
        <div style={{
          backgroundColor: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 10, overflow: "hidden",
          boxShadow: "0 1px 3px rgba(15,45,82,0.06)",
        }}>
          {/* Card header (match profile) */}
          <div style={{
            padding: "1rem 1.5rem",
            borderBottom: `1px solid ${C.borderLight}`,
            display: "flex", alignItems: "center",
            justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem",
            backgroundColor: C.cardHeader,
          }}>
            <div>
              <h3 style={{
                margin: 0, fontSize: "1rem",
                fontWeight: 700, color: C.cardHeaderText, fontFamily: SANS,
              }}>
                Score Breakdown
              </h3>
              <p style={{ margin: "0.15rem 0 0", fontSize: "0.84rem", color: C.textSecondary, fontFamily: SANS }}>
                Input values, scores (0–3), and reasoning per dimension
              </p>
            </div>
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
              {CATEGORY_ORDER.map((cat) => {
                const meta = CATEGORY_META[cat];
                return (
                  <span key={cat} style={{
                    fontSize: "0.76rem", fontWeight: 600,
                    padding: "0.22rem 0.65rem", borderRadius: 20,
                    backgroundColor: `${meta.color}12`,
                    color: meta.color,
                    border: `1.5px solid ${meta.color}28`,
                    fontFamily: SANS,
                  }}>
                    {cat}
                  </span>
                );
              })}
            </div>
          </div>

          {CATEGORY_ORDER.map((cat, idx) => (
            <CategorySection
              key={cat}
              title={cat}
              items={merged[cat]}
              categoryScore={
                cat === "Valuation"       ? scoreAudit?.valCategoryScore
                : cat === "Growth"        ? scoreAudit?.growthCategoryScore
                : cat === "Profitability" ? scoreAudit?.profitCategoryScore
                : scoreAudit?.healthCategoryScore
              }
              defaultOpen={idx === 0}
            />
          ))}

          {scoreAudit && (
            <div style={{
              padding: "0.85rem 1.5rem",
              fontSize: "0.82rem", color: C.textSecondary,
              backgroundColor: C.cardHeader, borderTop: `1px solid ${C.borderLight}`,
              fontFamily: SANS,
            }}>
              <Icon name="info circle" style={{ color: C.textMuted, margin: "0 0.35rem 0 0" }} />
              <strong style={{ color: C.textPrimary }}>Weighted formula: </strong>
              0.30×Profitability + 0.30×Financial Health + 0.25×Valuation + 0.15×Growth → scaled to 0–39
            </div>
          )}
        </div>
      ) : (
        <div style={{
          padding: "3rem 1.5rem", textAlign: "center",
          backgroundColor: C.bg, borderRadius: 10,
          border: `1px solid ${C.border}`,
        }}>
          <Icon name="bar chart" size="large" style={{ color: C.textMuted, marginBottom: "0.75rem" }} />
          <p style={{ margin: 0, fontSize: "0.95rem", color: C.textSecondary, lineHeight: 1.6, fontFamily: SANS }}>
            Run the daily calc job to generate the full score breakdown.
          </p>
        </div>
      )}
    </div>
  );
}